import { useEffect, useState } from 'react';
import useGameStore, { districtRentFor, districtSetOf, floatSharesOf, hasMonopoly, ownershipPct, rentYieldAtLevel, rentYieldOf, upgradeCostFor } from '../store/gameStore';
import type { PropertyTile } from '../types/gameTypes';
import { money, percent } from '../lib/format';
import { DISTRICT_META } from './districtMeta';
import { EquityBar, EquityLegend, districtEquitySegmentsOf, equitySegmentsOf } from './CapTable';
import { DistrictIcon } from './GameIcons';
import { tileName as localizeTile, useT } from '../i18n';

export const TIER_NAMES = ['Vacant Lot', 'Corner Shop', 'Apartment Block', 'Office Tower', 'Grand Complex', 'Megastructure'];
const PHASE_MUL = (phase: string) => (phase === 'BOOM' ? 1.5 : phase === 'CRISIS' ? 0.5 : 1);
/** Rent projection for a hypothetical tier: effective (tier-boosted) yield × valuation. */
export const tierRent = (tile: PropertyTile, level: number, phase: string) =>
  Math.round(rentYieldAtLevel(tile, level) * (tile.valuation > 0 ? tile.valuation : tile.baseCost) * PHASE_MUL(phase));

/** Cumulative build cost to reach a future tier (each tier costs 1.25× the previous one). */
export const tierCost = (tile: PropertyTile, level: number) => {
  if (level <= tile.upgradeLevel) return 0;
  let unit = tile.upgradeCost;
  let total = 0;
  for (let l = tile.upgradeLevel + 1; l <= level; l += 1) {
    total += unit;
    unit *= 1.25;
  }
  return Math.round(total);
};

export const DeltaTag = ({ pct }: { pct: number }) => {
  if (!pct) return <span className="text-[10px] text-slate-500">(— 0.0%)</span>;
  const up = pct > 0;
  return (
    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${up ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
};
interface Props { tile: PropertyTile | null; tileIndex: number | null; onClose: () => void; onBuy: (tile: PropertyTile) => void; onSell: (tile: PropertyTile) => void; }
const TileInspector = ({ tile, tileIndex, onClose, onBuy, onSell }: Props) => {
  const store = useGameStore();
  const t = useT();
  const { board, players, market, currentPlayerIndex } = store;
  // Hotseat: share/equity actions belong to the human at the controls (if any).
  const active = players[currentPlayerIndex];
  const human = active && !active.isAI ? active : players[0];
  const [segSel, setSegSel] = useState<string | null>(null);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);
  useEffect(() => setSegSel(null), [tile?.id]);
  if (!tile || !human) return null;
  const meta = DISTRICT_META[tile.district];
  const float = floatSharesOf(tile);
  const total = Math.max(1, tile.totalShares);
  const standingHere = tileIndex !== null && human.currentTileIndex === tileIndex;
  const humanShares = tile.shares[human.id] ?? 0;
  const humanPct = ownershipPct(tile, human.id);
  const isCommercial = tile.tileType === 'COMMERCIAL';
  // Colour-group size comes from the live board — never a hardcoded 5/6.
  const set = districtSetOf(board, human.id, tile.district);
  const mates = set.tiles;
  const synergyDone = set.complete;
  const curRent = districtRentFor(tile, market.phase, board);
  const yourCut = Math.round((curRent * humanPct) / 100);
  const allSegs = [...players.map((p) => ({ id: p.id, name: p.name, color: p.color, shares: tile.shares[p.id] ?? 0 })),
    { id: '__float', name: 'Public float', color: '#475569', shares: float }].filter((s) => s.shares > 0);
  // 10-player cap table: the visual bar collapses to top 3 holders + "Others".
  const holders = allSegs.filter((s) => s.id !== '__float').sort((a, b) => b.shares - a.shares);
  const barSegs =
    holders.length > 4
      ? [...holders.slice(0, 3), { id: '__others', name: 'Others', color: '#94a3b8', shares: holders.slice(3).reduce((s, h) => s + h.shares, 0) }]
      : allSegs;
  const segs = allSegs;
  const selSeg = segs.find((s) => s.id === segSel) ?? null;
  const proj25 = Math.round((curRent * Math.min(100, ((humanShares + Math.min(25, float)) / total) * 100)) / 100);
  const nextLvl = Math.min(5, tile.upgradeLevel + 1);
  const nextCost = upgradeCostFor(board, tile, human.id, nextLvl);
  const monopolyHere = isCommercial && hasMonopoly(board, human.id, tile.district);
  const freeUpgradeReady = Boolean(active && !active.isAI) && monopolyHere && human.lastFreeUpgradeRound !== market.currentRound && tile.upgradeLevel < 5 && !tile.isFrozen;
  const canDirect = humanPct > 50 && human.cash >= nextCost && tile.upgradeLevel < 5;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-slate-700/60 bg-slate-900 pb-safe shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="h-2.5 w-full" style={{ backgroundColor: meta.bar }} />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: meta.bar }}>{meta.label}</p>
              <h3 className="mt-1 flex items-center gap-2 text-xl font-extrabold text-white">
                <DistrictIcon district={tile.district} size={22} />
                {localizeTile(tile.name, t)}
              </h3>
              <p className="mt-0.5 text-xs text-slate-400">{tile.tileType} · Tier {tile.upgradeLevel}/5 · {TIER_NAMES[tile.upgradeLevel]}</p>
              {isCommercial && (
                <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="font-bold text-slate-100">{money(tile.valuation > 0 ? tile.valuation : tile.baseCost)}</span>
                  <DeltaTag pct={tile.valuationDeltaPct} />
                  <span className="rounded-full border border-violet-400/50 bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold text-violet-200">
                    Yield {(rentYieldOf(tile) * 100).toFixed(1)} % live · {money(curRent)}/visit
                  </span>
                </p>
              )}
              {standingHere && <p className="mt-1.5 inline-block rounded-full border border-emerald-400/50 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold text-emerald-300">You are standing here — no fuel penalty</p>}
              {/* Special tiles (transit / City Hall / financial desk / start) ship a full guide. */}
              {!isCommercial && (
                <button
                  type="button"
                  onClick={() => {
                    store.openTileGuide(tile.id);
                    onClose();
                  }}
                  className="mt-2 min-h-[36px] w-full rounded-xl border-2 border-cyan-400/70 bg-gradient-to-r from-cyan-600/90 to-sky-600/90 px-3 py-1.5 text-xs font-extrabold text-white transition hover:from-cyan-500 hover:to-sky-500"
                >
                  {t('action.tileGuide')}
                </button>
              )}
            </div>
            <button onClick={onClose} className="shrink-0 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-700">Close / ESC</button>
          </div>
          {isCommercial && mates.length > 1 && (
            <div className="mt-4 rounded-xl border border-slate-700/60 bg-slate-950/50 p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">District portfolio</p>
              {mates.map((m) => {
                const top = players.map((p) => ({ p, s: m.shares[p.id] ?? 0 })).sort((a, b) => b.s - a.s)[0];
                const lbl = top && top.s > 0 ? `${top.p.id === human.id ? 'You' : top.p.name} ${percent(ownershipPct(m, top.p.id), 0)}` : 'Unowned';
                return <p key={m.id} className={m.id === tile.id ? 'text-xs font-bold text-slate-100' : 'text-xs text-slate-400'}>{m.id === tile.id ? '▸ ' : '· '}{m.name} ({lbl})</p>;
              })}
              <div className="mt-2 flex items-center justify-between text-[11px]"><span className="text-slate-400">Hold &gt;50 % of ALL {mates.length} district shares → monopoly</span><span className={synergyDone ? 'font-bold text-emerald-300' : 'text-slate-300'}>{set.pct.toFixed(1)} %{synergyDone ? ' ✓ MONOPOLY' : ''}</span></div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-800"><div className={`h-full ${synergyDone ? 'bg-emerald-400' : 'bg-cyan-400'}`} style={{ width: `${Math.min(100, set.pct)}%` }} /></div>
              {synergyDone && (
                <div className="mt-2 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-2 py-1.5 text-[10px] font-semibold text-emerald-200">
                  Monopoly benefits: +25 % rent · −25 % upgrade cost · 1 free upgrade/round · zoning-freeze immunity
                </div>
              )}
              {/* District-level ownership split (balanced group of 3–5 properties). */}
              <EquityBar segments={districtEquitySegmentsOf(mates, players)} height="h-2" className="mt-2" />
              <EquityLegend segments={districtEquitySegmentsOf(mates, players)} limit={4} />
            </div>
          )}
          <div className="mt-3 rounded-xl border border-slate-700/60 bg-slate-950/50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Cap table</p>
            {/* Persistent ownership bar: player colours + gray unowned float. */}
            <EquityBar segments={equitySegmentsOf(tile, players)} height="h-2" className="mt-2" />
            <EquityLegend segments={equitySegmentsOf(tile, players)} limit={4} />
            <p className="mt-2 text-[10px] uppercase tracking-widest text-slate-600">Interactive selector</p>
            <div className="mt-1 flex h-4 w-full overflow-hidden rounded-full bg-slate-800">
              {barSegs.map((s) => (
                <button key={s.id} title={`${s.name}: ${s.shares}`} onClick={() => { const target = holders.find((h) => h.id === s.id) ?? (s.id === '__float' ? allSegs.find((a) => a.id === '__float') : null); if (target) setSegSel(target.id === segSel ? null : target.id); }} className="h-full hover:opacity-80" style={{ width: `${(s.shares / total) * 100}%`, backgroundColor: s.color }} />
              ))}
            </div>
            <div className="mt-1.5 max-h-44 space-y-0.5 overflow-y-auto text-xs">
              {segs.map((s) => (
                <button key={s.id} onClick={() => setSegSel(s.id === segSel ? null : s.id)} className={`flex w-full items-center gap-2 rounded px-1 py-0.5 text-left ${segSel === s.id ? 'bg-slate-800' : ''}`}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="flex-1 text-slate-200">{s.name}</span>
                  <span className="text-slate-400">{s.shares}/{tile.totalShares} · {percent((s.shares / total) * 100)}</span>
                </button>
              ))}
            </div>
            {selSeg && <p className="mt-1.5 rounded-lg bg-slate-800/70 px-2 py-1 text-[11px] text-slate-200">{selSeg.name}: <b>{selSeg.shares} shares</b> · vote <b>{percent((selSeg.shares / total) * 100)}</b></p>}
            <div className="mt-2 rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-2 text-xs">
              {humanShares > 0
                ? <p className="text-cyan-100">Your Share: <b>{percent(humanPct)}</b> · Your Cut Per Visit: <b>{money(yourCut)}</b></p>
                : <p className="text-cyan-100">You hold 0 shares · Projected cut if buying 25 shares: <b>{money(proj25)}/visit</b></p>}
            </div>
          </div>
          {isCommercial && (
            <div className="mt-3 rounded-xl border border-slate-700/60 bg-slate-950/50 p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Upgrade roadmap 0 → 5</p>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full min-w-[420px] text-left text-[11px]">
                  <thead><tr className="text-slate-500">
                    <th className="pb-1 pr-2 font-semibold">Tier</th>
                    <th className="pb-1 pr-2 font-semibold">Cost</th>
                    <th className="pb-1 pr-2 font-semibold">Rent</th>
                    <th className="pb-1 font-semibold">Yield</th>
                  </tr></thead>
                  <tbody>
                    {[0, 1, 2, 3, 4, 5].map((lvl) => {
                      const isCur = lvl === tile.upgradeLevel;
                      const isNext = lvl === tile.upgradeLevel + 1 && tile.upgradeLevel < 5;
                      const r = tierRent(tile, lvl, market.phase);
                      const c = tierCost(tile, lvl);
                      const y = rentYieldAtLevel(tile, lvl) * 100;
                      return (
                        <tr key={lvl} className={isCur ? 'text-slate-100' : isNext ? 'text-amber-100' : 'text-slate-500'}>
                          <td className="py-1 pr-2">
                            <span className="font-bold">T{lvl}</span> {TIER_NAMES[lvl]}{' '}
                            {isCur && <span className="ml-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold text-slate-950">CURRENT</span>}
                            {isNext && <span className="ml-1 rounded-full border border-amber-300/70 px-1.5 py-0.5 text-[9px] font-bold text-amber-200">NEXT TARGET</span>}
                          </td>
                          <td className={`py-1 pr-2 font-semibold ${isCur || isNext ? '' : 'opacity-60'}`}>{lvl <= tile.upgradeLevel ? '—' : money(c)}</td>
                          <td className={`py-1 pr-2 ${isCur || isNext ? '' : 'opacity-60'}`}>{money(r)}</td>
                          <td className={`py-1 font-semibold ${isCur || isNext ? '' : 'opacity-60'}`}>{y.toFixed(1)} %</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {tile.upgradeLevel >= 5 && <p className="mt-1 text-[11px] text-emerald-300">Max tier reached — Megastructure complete.</p>}
            </div>
          )}
          {isCommercial && (
            <div className="mt-4 space-y-2 border-t border-slate-700/60 pt-3">
              {(() => {
                // Strict acquisition rule: buy the public float only when physically
                // standing on this tile OR already holding a >50% majority (allowed
                // to consolidate the rest from anywhere on the board).
                const canBuyFloat = float > 0 && (standingHere || humanPct > 50);
                return (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {float > 0 && (
                        <span title={canBuyFloat ? undefined : 'Can only acquire shares when standing on this tile or holding >50% majority ownership (otherwise acquire via P2P Trade or Auction)'}>
                          <button
                            disabled={!canBuyFloat}
                            onClick={() => onBuy(tile)}
                            className="w-full rounded-xl bg-gradient-to-r from-cyan-400 to-sky-400 px-4 py-2.5 text-sm font-extrabold text-slate-950 disabled:cursor-not-allowed disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-400"
                          >
                            Buy Shares (Slider)
                          </button>
                        </span>
                      )}
                      {humanShares > 0 && <button onClick={() => onSell(tile)} className="rounded-xl bg-gradient-to-r from-rose-400 to-orange-400 px-4 py-2.5 text-sm font-extrabold text-slate-950">Sell Shares · {humanShares} owned</button>}
                    </div>
                    {float > 0 && !canBuyFloat && (
                      <p className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-amber-200">
                        Can only acquire shares when standing on this tile or holding &gt;50% majority ownership (otherwise acquire via P2P Trade or Auction)
                      </p>
                    )}
                  </>
                );
              })()}
              {float <= 0 && humanShares > 0 && <button onClick={() => onSell(tile)} className="w-full rounded-xl bg-gradient-to-r from-rose-400 to-orange-400 px-4 py-2.5 text-sm font-extrabold text-slate-950">Sell Shares (Slider) · {humanShares} owned</button>}
              {tile.upgradeLevel < 5 && canDirect && <button onClick={() => { store.proposeUpgrade(tile.id, human.id, nextLvl); onClose(); }} className="w-full rounded-xl bg-gradient-to-r from-emerald-400 to-teal-300 px-4 py-2.5 text-sm font-extrabold text-slate-950">Upgrade to Tier {nextLvl} for {money(nextCost)}{monopolyHere ? ' · −25 % monopoly discount' : ''} · majority auto-executes</button>}
              {tile.upgradeLevel < 5 && !canDirect && humanShares > 0 && <button onClick={() => { store.proposeUpgrade(tile.id, human.id, nextLvl); onClose(); }} className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2.5 text-sm font-extrabold text-white">Propose Shareholder Vote for Upgrade → T{nextLvl}{monopolyHere ? ' (−25 % monopoly discount)' : ''}</button>}
              {freeUpgradeReady && (
                <button onClick={() => { store.claimMonopolyFreeUpgrade(tile.id); onClose(); }} className="w-full rounded-xl bg-gradient-to-r from-amber-300 to-orange-400 px-4 py-2.5 text-sm font-extrabold text-slate-950">
                  Claim free monopoly upgrade → T{nextLvl}
                </button>
              )}
              {tile.upgradeLevel < 5 && humanShares === 0 && !canDirect && <p className="text-[11px] text-slate-500">Buy shares first to unlock upgrade votes.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default TileInspector;
